import { Component } from '@theme/component';
import { prefersReducedMotion, isRTL } from '@theme/utilities';

/*
 * Longevita storefront components.
 * Every component renders complete, usable markup from Liquid; this module only enhances it.
 */

/* ---------------------------------------------------------------------------
 * Reveal on scroll
 * Content is only hidden after this module confirms it can reveal it again.
 * ------------------------------------------------------------------------ */

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @param {ParentNode} root */
function observeReveals(root) {
  if (!revealObserver) return;

  for (const element of root.querySelectorAll('[data-lv-reveal]:not(.is-visible)')) {
    revealObserver.observe(element);
  }
}

if ('IntersectionObserver' in window && !prefersReducedMotion()) {
  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        revealObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  document.documentElement.classList.add('lv-reveal-ready');
  observeReveals(document);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observeReveals(event.target);
  });
}

/* ---------------------------------------------------------------------------
 * <lv-carousel>: horizontal scroller with previous / next buttons.
 * With `data-autoplay="<ms>"` it advances one item per interval and returns to the first item
 * after the last. Autoplay pauses on hover, focus, touch, off-screen and hidden tabs, can be
 * paused with the optional toggle button, and never runs with reduced motion.
 * ------------------------------------------------------------------------ */

/**
 * @typedef {object} LvCarouselRefs
 * @property {HTMLElement} track - The scroll container.
 * @property {HTMLButtonElement} [prev] - Previous button.
 * @property {HTMLButtonElement} [next] - Next button.
 * @property {HTMLButtonElement} [toggle] - Autoplay pause / play button.
 *
 * @extends Component<LvCarouselRefs>
 */
class LvCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #timer = 0;

  /** Reasons autoplay is currently held back (focus, touch, offscreen). */
  #holds = new Set();

  /** Paused by the visitor through the toggle button. */
  #userPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    window.clearInterval(this.#timer);
  }

  #bind() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    this.#updateButtons();
    this.#bindAutoplay(signal);
  }

  /** @param {AbortSignal} signal */
  #bindAutoplay(signal) {
    this.#observer?.disconnect();
    window.clearInterval(this.#timer);
    this.#holds.clear();

    const delay = Number(this.dataset.autoplay);
    if (!Number.isFinite(delay) || delay <= 0 || prefersReducedMotion()) return;

    const { toggle } = this.refs;
    if (toggle) toggle.hidden = false;
    this.#syncToggle();

    /** @param {string} reason @param {boolean} active */
    const hold = (reason, active) => {
      if (active) {
        this.#holds.add(reason);
      } else {
        this.#holds.delete(reason);
      }
      this.#restartAutoplay();
    };

    this.addEventListener('focusin', () => hold('focus', true), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) hold('focus', false);
      },
      { signal }
    );
    this.refs.track.addEventListener('touchstart', () => hold('touch', true), { passive: true, signal });
    for (const type of ['touchend', 'touchcancel']) {
      this.refs.track.addEventListener(type, () => hold('touch', false), { passive: true, signal });
    }
    document.addEventListener('visibilitychange', () => this.#restartAutoplay(), { signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) hold('offscreen', !entry.isIntersecting);
      });
      this.#observer.observe(this);
    }

    this.#restartAutoplay();
  }

  /** Restarts the interval from zero, or stops it while anything holds autoplay back. */
  #restartAutoplay() {
    window.clearInterval(this.#timer);

    const delay = Number(this.dataset.autoplay);
    if (this.#userPaused || this.#holds.size > 0 || document.hidden) return;

    this.#timer = window.setInterval(() => this.#advance(), delay);
  }

  /** Moves one item forward, or back to the first item after the last. */
  #advance() {
    // Hover is read per tick: enter/leave events go stale when the page scrolls under a still mouse,
    // and touch screens keep a sticky :hover after a tap.
    if (window.matchMedia('(hover: hover)').matches && this.matches(':hover')) return;

    const { track } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 4) return;

    if (Math.abs(track.scrollLeft) >= max - 4) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.#scroll(1);
    }
  }

  #syncToggle() {
    const { toggle } = this.refs;
    this.toggleAttribute('data-autoplay-paused', this.#userPaused);
    if (!toggle) return;

    const label = this.#userPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
    if (label) toggle.setAttribute('aria-label', label);
  }

  toggleAutoplay() {
    this.#userPaused = !this.#userPaused;
    this.#syncToggle();
    this.#restartAutoplay();
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateButtons());
  };

  #updateButtons() {
    const { track, prev, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const scrollable = max > 4;

    this.toggleAttribute('data-scrollable', scrollable);
    if (prev) prev.disabled = !scrollable || position <= 4;
    if (next) next.disabled = !scrollable || position >= max - 4;
  }

  /** Width of one item plus the track gap. */
  #step() {
    const { track } = this.refs;
    const item = track.firstElementChild;
    if (!(item instanceof HTMLElement)) return track.clientWidth;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  /** @param {number} direction */
  #scroll(direction) {
    const multiplier = isRTL(this) ? -1 : 1;
    this.refs.track.scrollBy({
      left: this.#step() * direction * multiplier,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  previous() {
    this.#scroll(-1);
  }

  next() {
    this.#scroll(1);
  }
}

/* ---------------------------------------------------------------------------
 * <lv-counter>: counts up to its final value when scrolled into view.
 * Markup already contains the final value for no-JS and reduced-motion users.
 * ------------------------------------------------------------------------ */

class LvCounter extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    const target = Number(this.dataset.value);
    if (!Number.isFinite(target) || prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    if (this.dataset.animated === 'true') return;

    this.textContent = this.#format(0);
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#animate(target);
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {number} value */
  #format(value) {
    const decimals = Number(this.dataset.decimals) || 0;
    return value.toLocaleString(document.documentElement.lang || undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  /** @param {number} target */
  #animate(target) {
    const duration = Number(this.dataset.duration) || 1600;
    const start = performance.now();

    /** @param {number} now */
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.textContent = this.#format(target * eased);

      if (progress < 1) {
        this.#frame = requestAnimationFrame(tick);
      } else {
        this.dataset.animated = 'true';
      }
    };

    this.#frame = requestAnimationFrame(tick);
  }
}

/* ---------------------------------------------------------------------------
 * <lv-gallery>: product image slider with thumbnails.
 * ------------------------------------------------------------------------ */

/**
 * @typedef {object} LvGalleryRefs
 * @property {HTMLElement} track - Slides scroll container.
 * @property {HTMLElement[]} [slides] - Slides.
 * @property {HTMLButtonElement[]} [thumbs] - Thumbnail buttons.
 *
 * @extends Component<LvGalleryRefs>
 */
class LvGallery extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #bind() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.refs.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#syncThumbs());
      },
      { passive: true, signal: this.#abortController.signal }
    );
  }

  get #index() {
    const { track } = this.refs;
    return Math.round(Math.abs(track.scrollLeft) / Math.max(track.clientWidth, 1));
  }

  #syncThumbs() {
    const index = this.#index;
    for (const [position, thumb] of (this.refs.thumbs ?? []).entries()) {
      if (position === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /** @param {number} index */
  goTo(index) {
    const slides = this.refs.slides ?? [];
    const count = slides.length;
    if (!count) return;

    const target = slides[(index + count) % count];
    if (!target) return;

    const multiplier = isRTL(this) ? -1 : 1;
    this.refs.track.scrollTo({
      left: target.offsetLeft * multiplier,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  previous() {
    this.goTo(this.#index - 1);
  }

  next() {
    this.goTo(this.#index + 1);
  }

  /** @param {Event} event */
  selectThumb(event) {
    const thumb = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (!(thumb instanceof HTMLElement)) return;
    this.goTo(Number(thumb.dataset.index));
  }
}

/* ---------------------------------------------------------------------------
 * <lv-buy-now>: the store has no cart. Submitting the product form opens a cart permalink
 * (`/cart/<variant>:<quantity>`), which starts a checkout with only that pack and leaves no
 * cart behind. Without JS the form posts to /cart/add with `return_to=/checkout`.
 * ------------------------------------------------------------------------ */

class LvBuyNow extends Component {
  /** @param {SubmitEvent} event - Routed by `on:submit`, so `event.target` is this element. */
  buy(event) {
    const form = this.querySelector('form');
    if (!form) return;

    const data = new FormData(form);
    const variantId = data.get('id');
    const quantity = Math.max(1, Number(data.get('quantity')) || 1);
    if (!variantId) return;

    event.preventDefault();

    const button = form.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) button.setAttribute('aria-busy', 'true');

    const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
    window.location.assign(`${cartUrl}/${variantId}:${quantity}`);
  }
}

// Returning from checkout through the back button restores the busy state from the bfcache.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const button of document.querySelectorAll('lv-buy-now button[aria-busy]')) {
    button.removeAttribute('aria-busy');
  }
});

/* ---------------------------------------------------------------------------
 * <lv-pack-picker>: radio cards that choose the variant submitted by the surrounding
 * <lv-buy-now> form. Each radio carries the variant id, price, installment amount and availability.
 * ------------------------------------------------------------------------ */

class LvPackPicker extends Component {
  /** @param {Event} event */
  select(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const productForm = this.closest('lv-buy-now');
    if (!productForm) return;

    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = input.value;

    const price = productForm.querySelector('[data-lv-atc-price]');
    if (price) price.textContent = input.dataset.price ?? '';

    const installment = productForm.querySelector('[data-lv-installments-amount]');
    if (installment) installment.textContent = input.dataset.installment ?? '';

    const available = input.dataset.available === 'true';
    const button = productForm.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !available;
      const label = button.querySelector('[data-lv-atc-label]');
      if (label) label.textContent = (available ? this.dataset.addLabel : this.dataset.soldOutLabel) ?? '';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', input.value);
    window.history.replaceState(window.history.state, '', url.toString());
  }
}

/* ---------------------------------------------------------------------------
 * <lv-timeline>: fills the center line as the visitor scrolls through the steps.
 * ------------------------------------------------------------------------ */

class LvTimeline extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    if (prefersReducedMotion()) {
      this.style.setProperty('--lv-progress', '1');
      return;
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const rect = this.getBoundingClientRect();
    const anchor = window.innerHeight * 0.6;
    const progress = Math.min(Math.max((anchor - rect.top) / Math.max(rect.height, 1), 0), 1);
    this.style.setProperty('--lv-progress', progress.toFixed(3));
  }
}

/* ---------------------------------------------------------------------------
 * <lv-hotspots>: image hotspots that open one note at a time.
 * ------------------------------------------------------------------------ */

class LvHotspots extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.closeAll();
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const open = this.querySelector('[aria-expanded="true"]');
        this.closeAll();
        if (open instanceof HTMLElement) open.focus();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  closeAll() {
    for (const button of this.querySelectorAll('[aria-expanded="true"]')) {
      button.setAttribute('aria-expanded', 'false');
    }
  }

  /** @param {Event} event */
  toggle(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('button');
    if (!button) return;

    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    this.closeAll();
    button.setAttribute('aria-expanded', String(willOpen));
  }
}

/* ---------------------------------------------------------------------------
 * <lv-reviews>: shows reviews one page at a time and filters them by photo or star rating.
 * Liquid renders every review visible; this component only hides the ones past the first page.
 * ------------------------------------------------------------------------ */

/**
 * @typedef {object} LvReviewsRefs
 * @property {HTMLElement} grid
 * @property {HTMLElement} [status]
 * @property {HTMLButtonElement} [more]
 */

/** @extends {Component<LvReviewsRefs>} */
class LvReviews extends Component {
  requiredRefs = ['grid'];

  /** Number of matching reviews currently shown. */
  #shown = 0;

  /** @type {string} */
  #filter = 'all';

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#render(this.#perPage, false);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // The summary and filter menus are <details>: close them on outside clicks and Escape.
    document.addEventListener(
      'click',
      (event) => {
        for (const menu of this.querySelectorAll('details[open]')) {
          if (event.target instanceof Node && !menu.contains(event.target)) menu.removeAttribute('open');
        }
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const menu = event.target instanceof Element ? event.target.closest('details[open]') : null;
        if (!menu) return;
        menu.removeAttribute('open');
        menu.querySelector('summary')?.focus();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render(Math.max(this.#shown, this.#perPage), false);
  }

  get #perPage() {
    const value = Number(this.dataset.perPage);
    return Number.isFinite(value) && value > 0 ? value : 20;
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.refs.grid.children]);
  }

  /** @param {HTMLElement} item */
  #matches(item) {
    if (this.#filter === 'all') return true;
    if (this.#filter === 'photos') return item.dataset.photo === 'true';
    return item.dataset.rating === this.#filter;
  }

  /**
   * @param {number} limit - How many matching reviews to show.
   * @param {boolean} announce - Whether to update the live region.
   */
  #render(limit, announce) {
    let matching = 0;
    let shown = 0;

    for (const item of this.#items) {
      const visible = this.#matches(item) && matching < limit;
      if (this.#matches(item)) matching += 1;
      if (visible) shown += 1;
      item.hidden = !visible;
    }

    this.#shown = shown;
    if (this.refs.more) this.refs.more.hidden = shown >= matching;

    if (announce && this.refs.status) {
      this.refs.status.textContent = (this.dataset.status ?? '')
        .replace('[shown]', String(shown))
        .replace('[total]', String(matching));
    }
  }

  showMore() {
    const firstNew = this.#items.filter((item) => item.hidden && this.#matches(item))[0];
    this.#render(this.#shown + this.#perPage, true);

    // Move focus to the first newly revealed review so keyboard users continue from there.
    const focusTarget = firstNew?.querySelector('article');
    if (focusTarget instanceof HTMLElement) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: false });
    }
  }

  /** @param {Event} event */
  filter(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.#filter = input.value;
    this.#render(this.#perPage, true);
  }
}

/* ---------------------------------------------------------------------------
 * <lv-countdown>: ticks down to `data-end` (Unix seconds). Liquid renders the initial values;
 * at zero the closest `[data-lv-promo]` element is hidden so expired offers never linger.
 * ------------------------------------------------------------------------ */

class LvCountdown extends HTMLElement {
  /** @type {number} */
  #timer = 0;

  connectedCallback() {
    const end = Number(this.dataset.end) * 1000;
    if (!Number.isFinite(end) || end <= 0) return;

    this.#tick(end);
    this.#timer = window.setInterval(() => this.#tick(end), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  /** @param {number} end */
  #tick(end) {
    const remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
    /** @type {Record<string, number>} */
    const values = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining % 86400) / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    };

    for (const element of this.querySelectorAll('[data-unit]')) {
      if (!(element instanceof HTMLElement)) continue;
      const value = values[element.dataset.unit ?? ''];
      if (value !== undefined) element.textContent = String(value).padStart(2, '0');
    }

    if (remaining > 0) return;

    window.clearInterval(this.#timer);
    const promo = this.closest('[data-lv-promo]');
    if (promo instanceof HTMLElement) promo.hidden = true;
  }
}

const definitions = /** @type {const} */ ([
  ['lv-carousel', LvCarousel],
  ['lv-counter', LvCounter],
  ['lv-gallery', LvGallery],
  ['lv-buy-now', LvBuyNow],
  ['lv-pack-picker', LvPackPicker],
  ['lv-timeline', LvTimeline],
  ['lv-hotspots', LvHotspots],
  ['lv-reviews', LvReviews],
  ['lv-countdown', LvCountdown],
]);

for (const [name, constructor] of definitions) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
